import type { AttachmentInfo } from '@api-client/api'
import { useLocalState } from '@hooks/useLocalState'
import {
	Box,
	Button,
	type ButtonBaseActions,
	FormHelperText,
} from '@mui/material'
import { SelectFileModal } from '@views/SelectFileModal'
import * as S from '@views/SelectFileModal/styles'
import {
	type ForwardedRef,
	forwardRef,
	useCallback,
	useImperativeHandle,
	useRef,
	useState,
} from 'react'
import { type FieldError, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Label } from './styles'

const SelectedFileItem = ({
	file,
	onRemove,
	t,
}: {
	file: AttachmentInfo
	onRemove: (file: AttachmentInfo) => void
	t: ReturnType<typeof useTranslation>['t']
}) => {
	const handleRemove = useCallback(() => onRemove(file), [file, onRemove])

	return (
		<Box display="flex" alignItems="center">
			<Box mr={2}>
				<S.NameWrapper>{file.name}</S.NameWrapper>
			</Box>
			<Button variant="text" color="primary" onClick={handleRemove}>
				{t('button.remove')}
			</Button>
		</Box>
	)
}

type AttachmentLike = Partial<AttachmentInfo>

const mapAttachmentLikeToInfo = (
	attachments: readonly AttachmentLike[]
): readonly AttachmentInfo[] =>
	attachments.map(attachment => ({
		id: attachment.id ?? '',
		name: attachment.name ?? '',
		createdAt: attachment.createdAt ?? '',
		isUsed: attachment.isUsed ?? false,
	}))

interface SelectFileProps {
	fieldError?: FieldError
	formFieldName?:
		| 'verificationData.verificationAttachments'
		| 'emissionsAttachments'
		| 'reportAttachments'
		| 'thirdPartyAuditAttachments'
		| 'additionalDocumentAttachments'
	label?: string
	showLabel?: boolean
	// This should be set to true if the form auto-saves when the form state is 'dirty'
	// If false, the form state will not be marked as dirty, so will not trigger the auto-save
	shouldDirtyOnChange?: boolean
	name?: string
	value?: readonly AttachmentLike[]
	onChange?: (value: unknown) => void
}

const SelectFile = (
	{
		fieldError,
		formFieldName,
		label,
		showLabel = false,
		shouldDirtyOnChange = true,
		name,
		value,
		onChange,
	}: SelectFileProps,
	ref: ForwardedRef<HTMLButtonElement>
) => {
	const { setValue } = useFormContext()
	const { t } = useTranslation()

	const targetFieldName = name ?? formFieldName
	const fallbackFieldName =
		formFieldName && formFieldName !== targetFieldName
			? formFieldName
			: undefined
	const [showModal, setShowModal] = useState<boolean>(false)
	const [selectedFiles, setSelectedFiles] = useLocalState(value, value =>
		Array.isArray(value) ? mapAttachmentLikeToInfo(value) : []
	)

	const selectFileButtonRef = useRef<HTMLButtonElement>(null)
	const selectFileButtonActions = useRef<ButtonBaseActions>(null)
	useImperativeHandle(
		ref,
		() => ({
			...(selectFileButtonRef.current as HTMLButtonElement),
			focus: () => {
				selectFileButtonRef.current?.focus()
				selectFileButtonActions.current?.focusVisible()
				selectFileButtonRef.current?.scrollIntoView({
					behavior: 'smooth',
					block: 'center',
				})
			},
		}),
		[]
	)

	const onSelectFile = useCallback(() => {
		setShowModal(true)
	}, [])

	const setAndUpdateAttachments = useCallback(
		(files: readonly AttachmentInfo[]) => {
			const dataFiles = files.map((file: AttachmentInfo) => ({
				id: file.id,
				name: file.name,
			}))

			if (targetFieldName) {
				setValue(targetFieldName, dataFiles, {
					shouldDirty: shouldDirtyOnChange,
				})
			}

			if (fallbackFieldName) {
				setValue(fallbackFieldName, dataFiles, {
					shouldDirty: shouldDirtyOnChange,
				})
			}

			if (onChange) {
				onChange(dataFiles)
			}

			// Only update the component's state after the setValue so it won't get updated if the step above fails.
			setSelectedFiles(files)
		},
		[
			targetFieldName,
			setValue,
			shouldDirtyOnChange,
			fallbackFieldName,
			onChange,
			setSelectedFiles,
		]
	)

	const onRemove = useCallback(
		(file: AttachmentInfo) => {
			const selectedIndex = selectedFiles.findIndex(
				(obj: AttachmentInfo) => obj.id === file.id
			)
			const newSelected = [
				...selectedFiles.slice(0, selectedIndex),
				...selectedFiles.slice(selectedIndex + 1),
			]
			setAndUpdateAttachments(newSelected)
		},
		[selectedFiles, setAndUpdateAttachments]
	)

	const showSelectedFiles =
		selectedFiles && selectedFiles.length > 0 && !showModal

	return (
		<Box>
			<SelectFileModal
				isModalOpened={showModal}
				selectedFiles={selectedFiles}
				onConfirm={setAndUpdateAttachments}
				onModalClose={setShowModal}
			/>
			{showLabel && label && (
				<Box mt={2}>
					<Label variant="caption">{label}</Label>
				</Box>
			)}
			<Box>
				{showSelectedFiles &&
					selectedFiles.map((file, key) => (
						<SelectedFileItem key={key} file={file} onRemove={onRemove} t={t} />
					))}
			</Box>
			<Button
				variant="outlined"
				color="primary"
				onClick={onSelectFile}
				action={selectFileButtonActions}
				ref={selectFileButtonRef}
			>
				{t('button.selectFile')}
			</Button>
			{fieldError && !showSelectedFiles && (
				<Box height={6}>
					<FormHelperText error>
						{fieldError.message || t('input.required')}
					</FormHelperText>
				</Box>
			)}
		</Box>
	)
}

export default forwardRef(SelectFile)
